import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Award, Star, Crown, Compass, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/seo";

const TIER_CONFIG = {
  Ambassador: { icon: Crown, color: "text-sandy", bg: "bg-sandy/10", border: "border-sandy/30" },
  Adventurer: { icon: Star, color: "text-canopy", bg: "bg-canopy/10", border: "border-canopy/30" },
  Explorer: { icon: Compass, color: "text-midnight/60", bg: "bg-midnight/5", border: "border-midnight/10" },
};

export default function LeaderboardPage() {
  const [board, setBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Adventure Ambassadors - Refer Friends & Earn Discounts",
    description: "Join the Keyz's Epic Adventure Tours referral program. Share Saint Lucia adventures with friends, earn higher discounts up to 15% off, and climb the Ambassador leaderboard.",
  });

  useEffect(() => {
    api.get("/referral-leaderboard").then(res => {
      setBoard(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6 max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sandy/10 mb-6">
          <Award className="w-8 h-8 text-sandy" />
        </div>
        <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-3">Referral Program</p>
        <h1 className="font-cormorant text-4xl sm:text-5xl font-light text-midnight mb-4" data-testid="leaderboard-title">
          Adventure Ambassadors
        </h1>
        <p className="font-manrope text-sm sm:text-base text-midnight/50 max-w-lg mx-auto">
          Share the magic of Saint Lucia. Earn higher discounts, unlock exclusive perks, and climb the leaderboard.
        </p>
      </section>

      {/* Tier Cards */}
      <section className="px-6 max-w-4xl mx-auto mb-16">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { tier: "Explorer", range: "0-2 referrals", discount: "5%", perk: "Base referral discount", icon: Compass },
            { tier: "Adventurer", range: "3-5 referrals", discount: "10%", perk: "Featured on leaderboard", icon: Star },
            { tier: "Ambassador", range: "6+ referrals", discount: "15%", perk: "Free tour credit earned", icon: Crown },
          ].map((t) => {
            const cfg = TIER_CONFIG[t.tier];
            return (
              <div key={t.tier} className={`p-6 border ${cfg.border} ${cfg.bg} text-center`} data-testid={`tier-card-${t.tier.toLowerCase()}`}>
                <t.icon className={`w-8 h-8 ${cfg.color} mx-auto mb-3`} />
                <h3 className="font-cormorant text-xl text-midnight mb-1">{t.tier}</h3>
                <p className="font-manrope text-xs text-midnight/40 mb-3">{t.range}</p>
                <p className="font-cormorant text-3xl text-midnight mb-2">{t.discount}</p>
                <p className="font-manrope text-xs text-midnight/50">discount for friends</p>
                <Separator className="my-3" />
                <p className="font-manrope text-xs text-midnight/60">{t.perk}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Leaderboard */}
      <section className="px-6 max-w-4xl mx-auto pb-24">
        <div className="mb-8">
          <p className="uppercase text-xs tracking-[0.25em] text-sandy font-manrope mb-2">Top Referrers</p>
          <h2 className="font-cormorant text-3xl font-light text-midnight">Leaderboard</h2>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 bg-midnight/5 animate-pulse" />)}
          </div>
        ) : board.length === 0 ? (
          <div className="text-center py-16 bg-white border border-midnight/5" data-testid="empty-leaderboard">
            <Award className="w-10 h-10 text-midnight/10 mx-auto mb-3" />
            <p className="font-cormorant text-xl text-midnight/30 mb-2">No referrals yet</p>
            <p className="font-manrope text-sm text-midnight/30">Be the first to share and climb the ranks!</p>
            <Link to="/tours" className="inline-flex items-center gap-1 mt-4 text-sandy font-manrope text-sm hover:underline">
              Browse Tours <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {board.map((entry, i) => {
              const cfg = TIER_CONFIG[entry.tier] || TIER_CONFIG.Explorer;
              const TierIcon = cfg.icon || Compass;
              return (
                <div key={i} className="flex items-center gap-4 p-4 bg-white border border-midnight/5" data-testid={`leaderboard-entry-${i}`}>
                  <div className={`w-10 h-10 flex items-center justify-center font-cormorant text-lg ${i < 3 ? 'text-sandy font-semibold' : 'text-midnight/30'}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-manrope text-sm font-medium text-midnight">{entry.name}</p>
                    <p className="font-manrope text-xs text-midnight/40">{entry.referral_count} referral{entry.referral_count !== 1 ? 's' : ''}</p>
                  </div>
                  <Badge className={`font-manrope text-[10px] ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                    <TierIcon className="w-2.5 h-2.5 mr-1" />
                    {entry.tier}
                  </Badge>
                  <span className="font-manrope text-sm font-semibold text-sandy">{entry.discount}% off</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
